// The floating chart-hover tooltip: a `position: fixed`, portaled surface
// that tracks the pointer, plus the hook each chart primitive wires onto its
// own pointer events to know WHICH bucket is under the cursor. Built once,
// shared by BarChart/LineAreaChart/DonutChart/Gauge, all opt-in via a prop
// (see each primitive's own `hoverTooltip` doc comment for why opt-in, not
// default-on: this package also ships to web/admin/seller/admin-internal,
// and turning every existing chart's tooltip behavior on unconditionally
// would silently restyle surfaces nobody asked to change).
//
// The bucket-index math lives in chart-hover.ts (pure, tested) — this file
// is the React/DOM half: portal, positioning-on-resize, and the
// coarse-pointer guard ("Touch/coarse pointers should not end up with a
// tooltip stuck on screen" — a touch tap has no hover state to leave from,
// so a tooltip armed by `pointerdown` would just stick until the next tap
// elsewhere; the fix is to never arm it for a non-mouse pointer at all).
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import {
  bucketIndexFromFraction,
  type BucketIndexMode,
  fractionAcross,
  placeTooltip,
} from "./chart-hover";
import "./chart-hover-tooltip.css";

export interface ChartHoverState {
  /** The bucket index under the pointer, or `null` when nothing is hovered
   *  (pointer left the chart, or hasn't entered it yet). */
  index: number | null;
  /** Pointer handlers to spread onto the element whose width/height define
   *  the plot area (the `<svg>` itself for BarChart/LineAreaChart — its
   *  `getBoundingClientRect()` is what `fractionAcross` measures against). */
  handlers: {
    onPointerMove: (event: React.PointerEvent<Element>) => void;
    onPointerLeave: (event: React.PointerEvent<Element>) => void;
  };
  /** Viewport-relative anchor point (the pointer's own position) for
   *  `<ChartHoverTooltip anchor={...}>` — `null` whenever `index` is. */
  anchor: { x: number; y: number } | null;
}

/**
 * Tracks which bucket (by index) is under the pointer as it moves across an
 * element, via the shared fraction -> bucket-index math. `enabled` is the
 * primitive's own `hoverTooltip` prop — when `false` this returns inert
 * handlers so the calling primitive can always spread them unconditionally
 * rather than branching at every call site.
 */
export function useChartHover(
  bucketCount: number,
  mode: BucketIndexMode,
  enabled: boolean,
): ChartHoverState {
  const [state, setState] = useState<{ index: number | null; x: number; y: number }>({
    index: null,
    x: 0,
    y: 0,
  });

  const onPointerMove = useCallback(
    (event: React.PointerEvent<Element>) => {
      if (!enabled) return;
      // A touch/pen pointer has no natural "hover" gesture to leave from —
      // arming the tooltip on one risks it sticking on screen after the
      // finger lifts (no pointerleave fires from a tap). Mouse only.
      if (event.pointerType !== "mouse") return;
      const rect = event.currentTarget.getBoundingClientRect();
      const fraction = fractionAcross(event.clientX, rect);
      const index = bucketIndexFromFraction(fraction, bucketCount, mode);
      setState({ index, x: event.clientX, y: event.clientY });
    },
    [enabled, bucketCount, mode],
  );

  const onPointerLeave = useCallback((event: React.PointerEvent<Element>) => {
    if (event.pointerType !== "mouse") return;
    setState((prev) => (prev.index === null ? prev : { ...prev, index: null }));
  }, []);

  return useMemo(() => {
    const anchor = state.index === null ? null : { x: state.x, y: state.y };
    return { index: state.index, handlers: { onPointerMove, onPointerLeave }, anchor };
  }, [state, onPointerMove, onPointerLeave]);
}

export interface ChartHoverTooltipProps {
  anchor: { x: number; y: number } | null;
  children: ReactNode;
}

/**
 * The floating surface itself: portaled to `document.body` and positioned
 * `fixed` (never `absolute` inside the card), so it is never clipped by a
 * card's `overflow` and always paints above any card content. Positioned via
 * `placeTooltip` (chart-hover.ts) against the tooltip's OWN measured size, so
 * it never covers the anchor point (the mark the reader is pointing at) and
 * never runs off the viewport edge.
 */
export function ChartHoverTooltip({ anchor, children }: ChartHoverTooltipProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  // Re-measure every time the tooltip (re)appears or its content changes
  // size (a bucket with more series than the previous one, say) — jsdom has
  // no layout engine so this is a no-op there (0x0), which is fine: the
  // pure placement math already degrades to a safe on-screen fallback for a
  // zero-sized tooltip (see chart-hover.test.ts).
  const measuredRef = useCallback((node: HTMLDivElement | null) => {
    ref.current = node;
    if (node) {
      const rect = node.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });
    }
  }, []);

  if (anchor === null) return null;

  const viewport =
    typeof window === "undefined"
      ? { width: 0, height: 0 }
      : { width: window.innerWidth, height: window.innerHeight };
  const pos = placeTooltip(anchor, size, viewport);

  return createPortal(
    <div
      ref={measuredRef}
      className="rc-chart-hover-tooltip"
      role="status"
      style={{ left: pos.x, top: pos.y }}
    >
      {children}
    </div>,
    document.body,
  );
}
